import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Table from "../../../components/Table";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import { ModalDelete } from "../../../components/ModalDelete";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { canUseGestao, hasPermission } from "../../../services/auth";

const USAGE_OPTIONS = [
    { label: 'Receber e pagar', value: 'both' },
    { label: 'Só receber', value: 'receivable' },
    { label: 'Só pagar', value: 'payable' },
];

const YES_NO_OPTIONS = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
];

const ACTIVE_OPTIONS = [
    { label: 'Ativa', value: true },
    { label: 'Inativa', value: false },
];

const getUsageLabel = usage => USAGE_OPTIONS.find(option => option.value === usage)?.label ?? usage;

export const PaymentMethodsTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [methods, setMethods] = useState([]);
    const [sefazCodes, setSefazCodes] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [methodToEdit, setMethodToEdit] = useState(null);
    const [methodToDelete, setMethodToDelete] = useState(null);

    const [name, setName] = useState('');
    const [sefazCode, setSefazCode] = useState('99');
    const [usage, setUsage] = useState('both');
    const [bankAccountId, setBankAccountId] = useState(null);
    const [isDefault, setIsDefault] = useState(false);
    const [active, setActive] = useState(true);

    const canManage = hasPermission('payment_terms');
    // Conta padrão e meio de pagamento da nota só servem ao módulo Gestão: a
    // conta pré-preenche título e baixa, o meio vai na NF-e.
    const hasGestao = canUseGestao();

    useEffect(() => {
        getMethods();
        if (hasGestao) {
            api.get(`/payment-method/sefaz-codes`).then(res => setSefazCodes(res.data.resources)).catch(renderError);
        }
        if (hasGestao && hasPermission(['view_finance', 'manage_finance'])) {
            api.get(`/finance-bank-account`).then(res => setBankAccounts(res.data.resources)).catch(() => null);
        }
    }, []);

    const getMethods = () => {
        setLoading(true);

        api.get(`/payment-method`).then(res => {
            setMethods(res.data.resources);
        }).catch(renderError).then(() => setLoading(false));
    };

    const openModal = method => {
        setMethodToEdit(method);
        setName(method?.name ?? '');
        setSefazCode(method?.sefaz_code ?? '99');
        setUsage(method?.usage ?? 'both');
        setBankAccountId(method?.finance_bank_account_id ?? null);
        setIsDefault(method?.is_default ?? false);
        setActive(method?.active ?? true);
        setShowModal(true);
    };

    const save = () => {
        setSaving(true);

        const data = {
            name,
            sefaz_code: sefazCode,
            usage,
            finance_bank_account_id: bankAccountId,
            is_default: isDefault,
            active,
        };

        const request = methodToEdit
            ? api.put(`/payment-method/${methodToEdit.id}`, data)
            : api.post(`/payment-method`, data);

        request.then(() => {
            toast.success('Forma salva');
            setShowModal(false);
            getMethods();
        }).catch(renderError).then(() => setSaving(false));
    };

    const deleteMethod = () => {
        setDeleting(true);

        api.delete(`/payment-method/${methodToDelete.id}`).then(() => {
            setMethodToDelete(null);
            getMethods();
        }).catch(renderError).then(() => setDeleting(false));
    };

    const getSefazLabel = code => sefazCodes.find(option => option.value === code)?.label ?? code;

    const rows = methods.map(method => ({
        data: [
            <>
                {method.name}
                {method.is_default && <span className="payment-conditions-badge">Padrão</span>}
            </>,
            ...(hasGestao ? [getSefazLabel(method.sefaz_code)] : []),
            getUsageLabel(method.usage),
            method.active ? 'Ativa' : 'Inativa',
        ],
        buttons: canManage ? [
            <Button type={`delete`} svg={`delete`} action={() => setMethodToDelete(method)} />,
        ] : [],
        action: canManage ? () => openModal(method) : null,
    }));

    return (
        <>
            <ModalDelete
                show={methodToDelete !== null}
                text={<p>Tem certeza que deseja apagar a forma <strong>{methodToDelete?.name}</strong>? Se ela já foi usada, inative em vez de apagar.</p>}
                action={deleteMethod}
                loading={deleting}
                close={() => setMethodToDelete(null)}
            />
            <Modal
                title={methodToEdit ? 'Editar forma de pagamento' : 'Nova forma de pagamento'}
                show={showModal}
                close={() => setShowModal(false)}
                footer={<Button type={`primary`} size={`small`} text={`Salvar`} action={save} loading={saving} />}
            >
                <div className="section">
                    <div className="row">
                        <div className="col-12">
                            <Input label={`Nome`} value={name} change={setName} hint={`Ex.: Boleto Itaú, Pix, Cartão via link.`} />
                        </div>
                    </div>
                    {hasGestao &&
                    <div className="row">
                        <div className="col-12">
                            <InputSelect
                                label={`Meio de pagamento na nota fiscal`}
                                options={sefazCodes}
                                value={sefazCode}
                                change={setSefazCode}
                                menuInBody
                                hint={`É o código que a nota fiscal leva. "99 — Outros" vai com o nome desta forma na descrição.`}
                            />
                        </div>
                    </div>
                    }
                    <div className="row">
                        <div className="col-6">
                            <InputSelect label={`Usar em`} options={USAGE_OPTIONS} value={usage} change={setUsage} menuInBody />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Situação`} options={ACTIVE_OPTIONS} value={active} change={setActive} menuInBody />
                        </div>
                    </div>
                    <div className="row">
                        {hasGestao &&
                            <div className="col-6">
                                <InputSelect
                                    label={`Conta de recebimento/pagamento`}
                                    options={[{ label: 'Nenhuma', value: null }, ...bankAccounts.map(account => ({ label: account.name, value: account.id }))]}
                                    value={bankAccountId}
                                    change={setBankAccountId}
                                    menuInBody
                                    hint={`Vem preenchida no título e na baixa.`}
                                />
                            </div>
                        }
                        <div className={hasGestao ? 'col-6' : 'col-12'}>
                            <InputSelect
                                label={`Forma padrão`}
                                options={YES_NO_OPTIONS}
                                value={isDefault}
                                change={setIsDefault}
                                menuInBody
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <div className="payment-conditions-toolbar">
                <p>
                    Como o dinheiro entra ou sai.{hasGestao && ' O meio de pagamento é o que a nota fiscal declara à SEFAZ.'}
                </p>
                {canManage &&
                    <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Nova forma`} action={() => openModal(null)} />
                }
            </div>
            <Table
                header={['Nome', ...(hasGestao ? ['Meio na nota fiscal'] : []), 'Usar em', 'Situação', ...(canManage ? [''] : [])]}
                rows={rows}
                loading={loading}
            />
        </>
    );
};

export default PaymentMethodsTab;
